import { PHARMACY_NPIS } from '../pharmacies';
import type { MedicationProduct, ProductDisclaimer } from '../shared';

import {
    MEDICARE_BRIDGE_PRICE_NOTE,
    SELF_PAY_PRICE_NOTE,
    SELF_PAY_JOURNEY_PRICE_NOTE,
} from './shared';

const makeDisclosures = (
    cashAssistanceText: string,
    discountedCashPriceText?: string
): MedicationProduct['disclosures'] => {
    const disclaimers: ProductDisclaimer[] = [
        {
            headlinePriceTypes: ['CopayAssistance'],
            slots: ['price-card'],
            content: [
                {
                    key: 'terms-and-conditions',
                    text: '<caption><sup>*</sup>Additional taxes and fees may apply.</caption>',
                },
            ],
        },
        {
            headlinePriceTypes: ['CashAssistance'],
            slots: ['price-card'],
            content: [
                {
                    key: 'terms-and-conditions',
                    text: cashAssistanceText,
                },
            ],
        },
        {
            headlinePriceTypes: ['Incentive'],
            slots: ['price-card'],
            content: [
                {
                    key: 'terms-and-conditions',
                    text: '<caption><sup>*</sup>Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed Foundayo™ for an approved use consistent with FDA-approved product labeling.</caption>',
                },
            ],
        },
        {
            headlinePriceTypes: ['PharmacyIncentive'],
            slots: ['price-card'],
            content: [
                {
                    key: 'terms-and-conditions',
                    text: '<caption><sup>*</sup>Additional taxes and fees may apply. The price shown is available  through {{pharmacyName}}. If you choose to send your prescription to a different pharmacy, your price may be different. You will be asked to certify conditions of purchase, including that you have been prescribed Foundayo™ for an approved use consistent with FDA-approved product labeling.</caption>',
                },
            ],
        },
        {
            headlinePriceTypes: ['MedicareBridge'],
            slots: ['price-card'],
            content: [
                {
                    key: 'eligibility',
                    text: '<caption><sup>*</sup>Eligibility based on <a href="https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge">Medicare GLP\u20111 Bridge Clinical Criteria</a>. Certain plan types excluded. Not a guarantee of coverage.</caption>',
                },
            ],
        },
    ];

    if (discountedCashPriceText) {
        disclaimers.push({
            headlinePriceTypes: ['DiscountedCashPrice'],
            slots: ['price-card'],
            content: [
                {
                    key: 'terms-and-conditions',
                    text: discountedCashPriceText,
                },
            ],
        });
    }

    return { disclaimers };
};

const disclosures = makeDisclosures(
    '<caption><sup>*</sup>Additional taxes and fees may apply.</caption>'
);

// 2.5mg and 5.5mg only
const disclosuresWithTemporaryPriceReduction = makeDisclosures(
    '<caption><sup>*</sup>Additional taxes and fees may apply. For a limited time, through December 31, 2026, the self-pay price of the 2.5 mg dose is temporarily reduced to $149 per month and the price of the 5.5 mg dosage is temporarily reduced to $199 per month.</caption>',
    '<caption><sup>*</sup>Additional taxes and fees may apply. The price shown is available through {{pharmacyName}}. If you choose to send your prescription to a different pharmacy, your price may be different. For a limited time, through December 31, 2026, the self-pay price of the 2.5 mg dose is temporarily reduced to $149 per month and the price of the 5.5 mg dosage is temporarily reduced to $199 per month.</caption>'
);

const metadata: MedicationProduct['metadata'] = {
    coverageCheckUrl: 'https://foundayo.lilly.com/insurance-check',
};

const makeBridgePriceCard = (
    price: number,
    savings?: number
) => `<flex justify="space-between">
    <subtitle2>Medicare GLP-1 Bridge</subtitle2>
    <subtitle2 data-testid="pharmacy-pricing-card-bridge-price">$50.00/month<sup>*</sup></subtitle2>
</flex>
<hr color="primary" />
<stack gap="xxxs">
    <flex justify="space-between">
        <subtitle2>Self-pay</subtitle2>
        <subtitle2 data-testid="pharmacy-pricing-card-self-pay-price">Starting at \$${price}/month<sup>‡</sup></subtitle2>
    </flex>
    ${savings ? `<body2>Save up to \$${savings} per month off list price with the Foundayo™ Self Pay Journey Program if eligible.</body2>` : ''}
</stack>`;

export const foundayoProducts: MedicationProduct[] = [
    {
        description: 'Foundayo™ 0.8mg Tablet',
        dosageForm: 'SOCT',
        formLabel: 'Pill',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 0.8,
        imageUrl: '/static/img/foundayo-bottle-0.8mg.png',
        imageOrientation: 'portrait',
        ndcs: [
            { ndc: '00002417801', packageQuantity: 1 },
            { ndc: '00002417831', packageQuantity: 1 },
            { ndc: '00002417862', packageQuantity: 1 },
        ],
        pricing: { selfPay: 149, regularPrice: 149 },
        headlinePrices: [
            { type: 'Cash', value: 649 },
            {
                type: 'CashAssistance',
                value: 149,
                qualifier: {
                    text: '/month',
                    description: { key: '', text: '' },
                },
            },
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'DiscountedCashPrice',
                value: 149,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
            {
                type: 'NonCoveredBenefit',
                value: 149,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'MedicareBridge',
                value: 50,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(149),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
        metadata,
    },
    {
        description: 'Foundayo™ 2.5mg Tablet',
        dosageForm: 'SOCT',
        formLabel: 'Pill',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 2.5,
        imageUrl: '/static/img/foundayo-bottle-2.5mg.png',
        imageOrientation: 'portrait',
        ndcs: [
            { ndc: '00002450301', packageQuantity: 1 },
            { ndc: '00002450331', packageQuantity: 1 },
        ],
        pricing: { selfPay: 149, regularPrice: 149 },
        headlinePrices: [
            { type: 'Cash', value: 649 },
            {
                type: 'CashAssistance',
                value: 149,
                qualifier: {
                    text: '/month',
                    description: { key: '', text: '' },
                },
            },
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'DiscountedCashPrice',
                value: 149,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
            {
                type: 'NonCoveredBenefit',
                value: 149,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'MedicareBridge',
                value: 50,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(149),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures: disclosuresWithTemporaryPriceReduction,
        metadata,
    },
    {
        description: 'Foundayo™ 5.5mg Tablet',
        dosageForm: 'SOCT',
        formLabel: 'Pill',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 5.5,
        imageUrl: '/static/img/foundayo-bottle-5.5mg.png',
        imageOrientation: 'portrait',
        ndcs: [
            { ndc: '00002479401', packageQuantity: 1 },
            { ndc: '00002479431', packageQuantity: 1 },
        ],
        pricing: { selfPay: 199, regularPrice: 199 },
        headlinePrices: [
            { type: 'Cash', value: 649 },
            {
                type: 'CashAssistance',
                value: 199,
                qualifier: {
                    description: { key: '', text: '' },
                    text: '/month',
                },
            },
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'DiscountedCashPrice',
                value: 199,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
            {
                type: 'NonCoveredBenefit',
                value: 199,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'MedicareBridge',
                value: 50,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(199),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures: disclosuresWithTemporaryPriceReduction,
        metadata,
    },
    {
        description: 'Foundayo™ 9mg Tablet',
        dosageForm: 'SOCT',
        formLabel: 'Pill',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 9,
        imageUrl: '/static/img/foundayo-bottle-9mg.png',
        imageOrientation: 'portrait',
        ndcs: [
            { ndc: '00002480301', packageQuantity: 1 },
            { ndc: '00002480331', packageQuantity: 1 },
        ],
        pricing: { selfPay: 299, regularPrice: 299 },
        headlinePrices: [
            { type: 'Cash', value: 649 },
            {
                type: 'CashAssistance',
                value: 299,
                qualifier: {
                    description: { key: '', text: '' },
                    text: '/month',
                },
            },
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'DiscountedCashPrice',
                value: 299,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
            {
                type: 'NonCoveredBenefit',
                value: 299,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'MedicareBridge',
                value: 50,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(299),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
        metadata,
    },
    {
        description: 'Foundayo™ 14.5mg Tablet',
        dosageForm: 'SOCT',
        formLabel: 'Pill',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 14.5,
        imageUrl: '/static/img/foundayo-bottle-14.5mg.png',
        imageOrientation: 'portrait',
        ndcs: [
            { ndc: '00002483901', packageQuantity: 1 },
            { ndc: '00002483931', packageQuantity: 1 },
        ],
        pricing: { selfPay: 299, regularPrice: 649 },
        headlinePrices: [
            { type: 'Cash', value: 649 },
            {
                type: 'CashAssistance',
                value: 349,
                qualifier: {
                    description: { key: '', text: '' },
                    text: '/month',
                },
            },
            {
                type: 'Incentive',
                value: 299,
                qualifier: {
                    text: '/month',
                },
            },
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'PharmacyIncentive',
                value: 299,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
            {
                type: 'NonCoveredBenefit',
                value: 299,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'MedicareBridge',
                value: 50,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(299, 350),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_JOURNEY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
        metadata,
    },
    {
        description: 'Foundayo™ 17.2mg Tablet',
        dosageForm: 'SOCT',
        formLabel: 'Pill',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 17.2,
        imageUrl: '/static/img/foundayo-bottle-17.2mg.png',
        imageOrientation: 'portrait',
        ndcs: [
            { ndc: '00002495301', packageQuantity: 1 },
            { ndc: '00002495331', packageQuantity: 1 },
        ],
        pricing: { selfPay: 299, regularPrice: 649 },
        headlinePrices: [
            { type: 'Cash', value: 649 },
            {
                type: 'CashAssistance',
                value: 349,
                qualifier: {
                    description: { key: '', text: '' },
                    text: '/month',
                },
            },
            {
                type: 'Incentive',
                value: 299,
                qualifier: {
                    text: '/month',
                },
            },
            {
                type: 'CopayAssistance',
                value: 25,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'PharmacyIncentive',
                value: 299,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
            },
            {
                type: 'NonCoveredBenefit',
                value: 299,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
            {
                type: 'MedicareBridge',
                value: 50,
                qualifier: {
                    text: '/month',
                },
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(299, 350),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_JOURNEY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.FUZE_HEALTH, PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
        metadata,
    },
];
